import { FrequencyOption } from './FrequencySelector';

export interface NotificationTypeFrequencyConfig {
  [key: string]: {
    label: string;
    options: FrequencyOption[];
    default: string;
    description?: string;
  };
}

export const FREQUENCY_CONFIG: NotificationTypeFrequencyConfig = {
  documentation_reminders: {
    label: 'Documentation Reminders',
    description: 'Reminders to document your work and achievements',
    default: 'weekly_end',
    options: [
      { value: 'daily', label: 'Daily', description: 'Every day at your preferred time' },
      { value: 'weekly_start', label: 'Weekly (Monday)', description: 'Start of each week' },
      { value: 'weekly_end', label: 'Weekly (Friday)', description: 'End of each week' },
      { value: 'biweekly', label: 'Every 2 weeks', description: 'Every other Friday' },
      { value: 'monthly', label: 'Monthly', description: 'Last Friday of each month' }
    ]
  },
  
  progress_summaries: {
    label: 'Progress Summaries',
    description: 'Weekly or monthly summaries of your career progress',
    default: 'weekly',
    options: [
      { value: 'weekly', label: 'Weekly', description: 'Every Sunday evening' },
      { value: 'biweekly', label: 'Every 2 weeks', description: 'Every other Sunday' },
      { value: 'monthly', label: 'Monthly', description: 'Last Sunday of each month' }
    ]
  },
  
  achievement_celebrations: {
    label: 'Achievement Celebrations',
    description: 'How you want to be notified about achievements',
    default: 'immediate',
    options: [
      { value: 'immediate', label: 'Immediately', description: 'Right when you earn an achievement' },
      { value: 'daily_digest', label: 'Daily digest', description: 'Once per day summary' },
      { value: 'weekly_digest', label: 'Weekly digest', description: 'Weekly summary on Sundays' }
    ]
  },
  
  goal_setting_prompts: {
    label: 'Goal Setting Prompts',
    description: 'Reminders to set and review your career goals',
    default: 'monthly',
    options: [
      { value: 'weekly', label: 'Weekly', description: 'Every Monday morning' },
      { value: 'monthly', label: 'Monthly', description: 'First Monday of each month' },
      { value: 'quarterly', label: 'Quarterly', description: 'Start of each quarter' }
    ]
  },
  
  reflection_reminders: {
    label: 'Reflection Reminders',
    description: 'Prompts to reflect on your career journey',
    default: 'monthly',
    options: [
      { value: 'weekly', label: 'Weekly', description: 'Every Sunday evening' },
      { value: 'monthly', label: 'Monthly', description: 'Last Sunday of each month' }
    ]
  },
  
  self_review: {
    label: 'Self Review',
    description: 'Reminders to conduct comprehensive self-reviews',
    default: 'quarterly',
    options: [
      { value: 'monthly', label: 'Monthly', description: 'Last Friday of each month' },
      { value: 'quarterly', label: 'Quarterly', description: 'End of each quarter' },
      { value: 'semi_annually', label: 'Semi-annually', description: 'Every 6 months' },
      { value: 'annually', label: 'Annually', description: 'Once per year' }
    ]
  },
  
  career_opportunities: {
    label: 'Career Opportunities',
    description: 'Reminders to review and pursue career opportunities',
    default: 'quarterly',
    options: [
      { value: 'monthly', label: 'Monthly', description: 'Last Friday of each month' },
      { value: 'quarterly', label: 'Quarterly', description: 'End of each quarter' },
      { value: 'semi_annually', label: 'Semi-annually', description: 'Every 6 months' },
      { value: 'annually', label: 'Annually', description: 'Once per year' }
    ]
  },
  
  milestone_celebrations: {
    label: 'Milestone Celebrations',
    description: 'How to celebrate major career milestones',
    default: 'immediate',
    options: [
      { value: 'immediate', label: 'Immediately', description: 'Right when milestone is reached' },
      { value: 'weekly_digest', label: 'Weekly digest', description: 'Include in weekly summary' }
    ]
  },
  
  streak_reminders: {
    label: 'Streak Reminders',
    description: 'Reminders about your activity streaks',
    default: 'weekly',
    options: [
      { value: 'daily', label: 'Daily', description: 'Daily streak updates' },
      { value: 'weekly', label: 'Weekly', description: 'Weekly streak summary' }
    ]
  },
  
  weekly_assessment_reminder: {
    label: 'Weekly Assessment Reminders',
    description: 'Reminders to complete your weekly internship assessment',
    default: 'weekly_end',
    options: [
      { value: 'weekly_mid', label: 'Mid-week', description: 'Every Wednesday' },
      { value: 'weekly_end', label: 'End of week', description: 'Every Friday' },
      { value: 'weekly_sunday', label: 'Sunday', description: 'Every Sunday evening' }
    ]
  },
  
  weekly_reflection_prompt: {
    label: 'Weekly Reflection Prompts',
    description: 'Prompts to reflect on your internship week',
    default: 'weekly_sunday',
    options: [
      { value: 'weekly_end', label: 'End of week', description: 'Every Friday afternoon' },
      { value: 'weekly_sunday', label: 'Sunday', description: 'Every Sunday evening' }
    ]
  },
  
  internship_goal_achieved: {
    label: 'Goal Achievement Notifications',
    description: 'Notifications when you achieve internship goals',
    default: 'immediate',
    options: [
      { value: 'immediate', label: 'Immediately', description: 'Right when goal is achieved' },
      { value: 'daily_digest', label: 'Daily digest', description: 'Daily summary of achievements' },
      { value: 'weekly_digest', label: 'Weekly digest', description: 'Weekly summary on Sundays' }
    ]
  },
  
  internship_phase_transition: {
    label: 'Phase Transition Updates',
    description: 'Updates when you enter a new internship phase',
    default: 'immediate',
    options: [
      { value: 'immediate', label: 'Immediately', description: 'As soon as you enter a new phase' },
      { value: 'weekly_start', label: 'Start of week', description: 'Monday morning update' }
    ]
  }
};

export const getFrequencyConfig = (notificationType: string) => {
  return FREQUENCY_CONFIG[notificationType] || null;
};

export const getFrequencyLabel = (notificationType: string, frequency: string): string => {
  const config = getFrequencyConfig(notificationType);
  if (!config) return frequency;
  
  const option = config.options.find(opt => opt.value === frequency);
  return option?.label || frequency;
};

export const getFrequencyDescription = (notificationType: string, frequency: string): string => {
  const config = getFrequencyConfig(notificationType);
  if (!config) return '';
  
  const option = config.options.find(opt => opt.value === frequency);
  return option?.description || '';
}; 